import { useRef, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Flex,
  Text,
  useDisclosure,
  usePrefersReducedMotion,
  useToast,
} from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useDispatch } from "react-redux";
import { rotateApiKey, refusalReason } from "api/ApiKeySettings";
import { storeUserApiKey } from "utils/auth/apiKeyStore";
import { copyToClipboard } from "utils/manipulation/maskApiKey";
import { FOCUS_RING } from "utils/chackra/controlStyles";
import Panel from "./Panel";

const ROTATE_REFUSALS = ["shared_key", "revocation_unavailable", "key_changed"];

/** Secondary action: brand text on the surface, a functional border. */
const secondaryButton = {
  variant: "outline",
  color: "brand.solid",
  borderColor: "border.strong",
  _hover: { bg: "bg.surfaceHover" },
  _focusVisible: FOCUS_RING,
} as const;

/** Destructive fill: `status.error` under its AA foreground, in both modes. */
const destructiveFill = {
  bg: "status.error",
  color: "text.inverse",
  _hover: { bg: "status.error", filter: "brightness(0.92)" },
  _active: { bg: "status.error", filter: "brightness(0.85)" },
  _focusVisible: FOCUS_RING,
} as const;

interface ApiKeyPanelProps {
  apiKey: string;
  /** What the page shows for the key right now (masked unless revealed). */
  shownKey: string;
  revealed: boolean;
  onRevealedChange: (revealed: boolean) => void;
}

/**
 * The key: masked by default, revealed and copied on explicit action, and
 * replaced through a confirmation that names the consequence (spec
 * 2026-10-20-website-api-settings, US2 / US4).
 */
const ApiKeyPanel = ({
  apiKey,
  shownKey,
  revealed,
  onRevealedChange,
}: ApiKeyPanelProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const toast = useToast();
  const reducedMotion = usePrefersReducedMotion();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [rotated, setRotated] = useState(false);
  const t = (id: string) => intl.formatMessage({ id });

  const copyKey = async () => {
    const ok = await copyToClipboard(apiKey);
    toast({
      position: "top",
      status: ok ? "success" : "error",
      title: t(ok ? "profileApi.key.copied" : "profileApi.key.copyFailed"),
      duration: 2000,
      isClosable: true,
    });
  };

  const openRotate = () => {
    setRotateError(null);
    onOpen();
  };

  const confirmRotate = async () => {
    setRotating(true);
    setRotateError(null);
    try {
      const newKey = await rotateApiKey(apiKey);
      // Every copy of the key (both redux slices, localStorage, the session
      // cookie) moves in one call; the old key is gone from the page.
      storeUserApiKey(dispatch, newKey);
      onRevealedChange(true);
      setRotated(true);
      onClose();
    } catch (error) {
      const reason = refusalReason(error);
      setRotateError(
        t(
          `profileApi.key.rotateError.${
            reason && ROTATE_REFUSALS.indexOf(reason) !== -1 ? reason : "unknown"
          }`
        )
      );
    } finally {
      setRotating(false);
    }
  };

  return (
    <Panel id="api-key" title={t("profileApi.key.heading")}>
      <Text fontSize="sm" color="text.secondary" mb={3} id="api-key-label">
        {t("profileApi.key.label")}
      </Text>
      <Box
        as="code"
        display="block"
        fontFamily="mono"
        fontSize="md"
        color="text.primary"
        bg="bg.page"
        borderWidth="1px"
        borderColor="border.subtle"
        borderRadius="md"
        px={3}
        py={2}
        wordBreak="break-all"
        aria-labelledby="api-key-label"
        data-testid="api-key-value"
      >
        {shownKey}
      </Box>

      <Flex gap={3} mt={4} wrap="wrap">
        <Button
          {...secondaryButton}
          onClick={() => onRevealedChange(!revealed)}
          aria-pressed={revealed}
        >
          {t(revealed ? "profileApi.key.hide" : "profileApi.key.reveal")}
        </Button>
        <Button colorScheme="blue" onClick={copyKey} _focusVisible={FOCUS_RING}>
          {t("profileApi.key.copy")}
        </Button>
        <Button {...secondaryButton} color="status.error" onClick={openRotate}>
          {t("profileApi.key.rotate")}
        </Button>
      </Flex>

      {rotated && (
        <Alert
          status="success"
          variant="left-accent"
          bg="status.successSubtle"
          borderColor="status.success"
          color="text.primary"
          borderRadius="md"
          mt={4}
          data-testid="api-key-rotated"
        >
          <AlertIcon color="status.success" />
          <AlertDescription fontSize="sm">
            {t("profileApi.key.rotated")} {t("profileApi.key.rotateBot")}
          </AlertDescription>
        </Alert>
      )}

      <Text fontSize="sm" color="text.secondary" mt={4}>
        {t("profileApi.key.note")}
      </Text>

      {/* An alertdialog: a destructive confirmation whose first focus is the
          least destructive action (Cancel). */}
      <AlertDialog
        isOpen={isOpen}
        onClose={rotating ? () => {} : onClose}
        leastDestructiveRef={cancelRef}
        motionPreset={reducedMotion ? "none" : "scale"}
        isCentered
      >
        <AlertDialogOverlay />
        <AlertDialogContent bg="bg.overlay" color="text.primary" mx={4}>
          <AlertDialogHeader>{t("profileApi.key.rotateTitle")}</AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={3}>{t("profileApi.key.rotateConsequence")}</Text>
            <Text fontSize="sm" color="text.secondary">
              {t("profileApi.key.rotateBot")}
            </Text>
            {rotateError && (
              <Alert
                status="error"
                variant="left-accent"
                bg="status.errorSubtle"
                borderColor="status.error"
                color="text.primary"
                borderRadius="md"
                mt={4}
                data-testid="api-key-rotate-error"
              >
                <AlertIcon color="status.error" />
                <AlertDescription fontSize="sm">{rotateError}</AlertDescription>
              </Alert>
            )}
          </AlertDialogBody>
          <AlertDialogFooter gap={3}>
            <Button
              ref={cancelRef}
              {...secondaryButton}
              onClick={onClose}
              isDisabled={rotating}
            >
              {t("profileApi.key.cancel")}
            </Button>
            <Button
              {...destructiveFill}
              onClick={confirmRotate}
              isLoading={rotating}
              loadingText={t("profileApi.key.rotating")}
            >
              {t("profileApi.key.rotateConfirm")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
};

export default ApiKeyPanel;
